import * as React from 'react';
import Box from '@mui/joy/Box';
import Typography from '@mui/joy/Typography';
import {CircularProgress} from '@mui/joy';
import PeopleAltRoundedIcon from '@mui/icons-material/PeopleAltRounded';
import EventNoteRoundedIcon from '@mui/icons-material/EventNoteRounded';
import PersonAddAlt1RoundedIcon from '@mui/icons-material/PersonAddAlt1Rounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import ChatListItem from "./ChatListItem";
import type { ChatProps } from "./Messages";

type CategoryOption = {
    value: string;
    label: string;
    Icon: React.ComponentType<{ fontSize?: 'inherit' | 'medium' | 'small' | 'large' }>;
};

const CATEGORY_OPTIONS: CategoryOption[] = [
    {value: 'All', label: 'All', Icon: PeopleAltRoundedIcon},
    {value: 'Existing Booking', label: 'Bookings', Icon: EventNoteRoundedIcon},
    {value: 'Sales', label: 'Leads', Icon: PersonAddAlt1RoundedIcon},
];

type ChatsPaneProps = {
    chats: ChatProps[];
    setSelectedChat: (chat: ChatProps) => void;
    selectedChatId: string;
    searchTerm: string;
    onSearchChange: (searchTerm: string) => void;
    category: string;
    onCategoryChange: (category: string) => void;
    loadMoreChats: () => void;
    hasMoreChats: boolean;
    isFetchingChats: boolean;
};

export default function ChatsPane(props: ChatsPaneProps) {
    const {chats, setSelectedChat, selectedChatId, searchTerm, onSearchChange, category, onCategoryChange, loadMoreChats, hasMoreChats, isFetchingChats} = props;

    const loadMoreRef = React.useRef(loadMoreChats);
    const hasMoreRef = React.useRef(hasMoreChats);
    const fetchingRef = React.useRef(isFetchingChats);
    loadMoreRef.current = loadMoreChats;
    hasMoreRef.current = hasMoreChats;
    fetchingRef.current = isFetchingChats;

    const sentinelRef = React.useRef<HTMLDivElement | null>(null);

    React.useEffect(() => {
        const sentinel = sentinelRef.current;
        if (!sentinel) return;

        const io = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && hasMoreRef.current && !fetchingRef.current) {
                    loadMoreRef.current();
                }
            },
            {rootMargin: '200px'},
        );
        io.observe(sentinel);
        return () => io.disconnect();
    }, []);

    return (
        <Box
            sx={{
                borderRight: '1px solid',
                borderColor: '#e5e7eb',
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
                minHeight: 0,
                height: '100%',
                overflow: 'hidden',
                minWidth: 300,
            }}
        >
            {/* Header */}
            <Box sx={{flexShrink: 0, p: 2, borderBottom: '1px solid', borderColor: '#e5e7eb', bgcolor: '#f9fafb'}}>
                <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5}}>
                    <Typography sx={{fontWeight: 500, fontSize: '16px'}}>Messages</Typography>
                </Box>

                {/* Filter chips */}
                <Box sx={{display: 'flex', gap: 0.5, mb: 1.5}}>
                    {CATEGORY_OPTIONS.map(opt => {
                        const isActive = category === opt.value;
                        return (
                            <Box
                                key={opt.value}
                                component="button"
                                onClick={() => onCategoryChange(opt.value)}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.75,
                                    px: 1.5,
                                    py: 0.75,
                                    fontSize: '12px',
                                    fontWeight: 500,
                                    borderRadius: '6px',
                                    border: isActive ? 'none' : '1px solid',
                                    borderColor: '#e5e7eb',
                                    cursor: 'pointer',
                                    transition: 'all 0.15s',
                                    ...(isActive
                                        ? {bgcolor: '#2563eb', color: '#fff', boxShadow: '0 1px 2px rgba(0,0,0,0.05)'}
                                        : {bgcolor: '#fff', color: '#4b5563', '&:hover': {bgcolor: '#f3f4f6'}}),
                                }}
                            >
                                <opt.Icon fontSize="small" sx={{fontSize: 14, color: isActive ? '#fff' : '#4b5563'}}/>
                                {opt.label}
                            </Box>
                        );
                    })}
                </Box>

                {/* Search */}
                <Box sx={{position: 'relative'}}>
                    <SearchRoundedIcon sx={{position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', fontSize: 16, color: '#9ca3af'}}/>
                    <Box
                        component="input"
                        type="text"
                        placeholder="Search name, email, phone..."
                        value={searchTerm}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => onSearchChange(e.target.value)}
                        sx={{
                            width: '100%',
                            pl: 4,
                            pr: 1.5,
                            py: 1,
                            fontSize: '14px',
                            border: '1px solid',
                            borderColor: '#e5e7eb',
                            borderRadius: '6px',
                            outline: 'none',
                            fontFamily: 'inherit',
                            '&:focus': {borderColor: '#3b82f6', boxShadow: '0 0 0 1px #3b82f6'},
                        }}
                    />
                </Box>
            </Box>

            {/* Scrollable list */}
            <Box sx={{flex: 1, overflowY: 'auto', minHeight: 0, WebkitOverflowScrolling: 'touch'}}>
                {chats.length === 0 && !isFetchingChats && (
                    <Box sx={{p: 2, textAlign: 'center', color: '#6b7280'}}>
                        <Typography level="body-sm" sx={{color: '#6b7280'}}>No conversations found</Typography>
                    </Box>
                )}

                {chats.map((chat) => (
                    <ChatListItem
                        key={chat.id}
                        {...chat}
                        setSelectedChat={setSelectedChat}
                        selectedChatId={selectedChatId}
                    />
                ))}

                {/* Sentinel element for infinite scroll — always in DOM */}
                <div ref={sentinelRef} style={{height: 1}} />

                {isFetchingChats && chats.length > 0 && (
                    <Box sx={{display: 'flex', justifyContent: 'center', p: 2}}>
                        <CircularProgress size="sm"/>
                    </Box>
                )}
            </Box>
        </Box>
    );
}
